import { reduce, isEmpty } from 'lodash';
import { onSnapshot } from 'firebase/firestore';
import { FirestoreChangeTypes } from '@sda/enums';
import { QueryHelper } from '../../query.helpers';
import { BaseHelpers } from '../base.helpers';
import { DefaultSnapshotHandler } from './DefaultSnapshotHandler';
import type {
  SnapshotQueryCallback, SnapshotQueryOptions, SnapshotQueryResult, SnapshotResult,
  FetchDataType, FirestoreClientData,
} from '@sda/sdk/interfaces';
import type FirebaseFirestore from 'firebase/firestore';
import type { Reducers } from '@sda/enums/store';
import type { UpsertItem } from '@sda/utils/store/interfaces';

export class SagaHelpers extends BaseHelpers {
  private reducerName: Reducers | ((item: any) => Reducers);

  constructor(clientData: FirestoreClientData, reducerName: Reducers | ((item: any) => Reducers)) {
    super(clientData);

    this.reducerName = reducerName;
  }

  defaultSnapshotCallback = <S>(item: SnapshotResult<FetchDataType, S>, options: { removeDeletedRows?: boolean } = {}): void => {
    if (!isEmpty(item)) {
      if (Object.prototype.hasOwnProperty.call(item, 'payload')) {
        if (typeof this.reducerName === 'function') {
          const { payload } = item as UpsertItem<FetchDataType>;
          const reducerCallback = this.reducerName;

          Object.values(payload).forEach((payloadItem) => {
            const handler = new DefaultSnapshotHandler(reducerCallback(payloadItem), options);
            handler.runHandler(payloadItem);
          });
        } else {
          const handler = new DefaultSnapshotHandler(this.reducerName, options);
          handler.runHandler(item);
        }
      } else if (Object.prototype.hasOwnProperty.call(item, 'id')) {
        if (typeof this.reducerName === 'function') {
          const handler = new DefaultSnapshotHandler(this.reducerName(item), options);
          handler.runHandler(item);
        } else {
          const handler = new DefaultSnapshotHandler(this.reducerName, options);
          handler.runHandler(item);
        }
      }
    } else if (typeof this.reducerName !== 'function') {
      const handler = new DefaultSnapshotHandler(this.reducerName, options);
      handler.runHandler(item);
    }
  };

  /**
    * Watch a record or collection for changes
    * NOTE: Not available for Admin Firestore
    * @param  {FirebaseFirestore.CollectionReference} collection
    * @param  {string} id
    * @param  {object[]} queries
    * @param  {string[]} changeTypes
    * @param  {number} limit
    * @param  {Function} callback
    * @return {Function}
    */
  watchRecord = <T, S>(
    collection: FirebaseFirestore.CollectionReference<T>,
    options: SnapshotQueryOptions<T>,
    snapshotCallback: SnapshotQueryCallback<T, S>,
  ): SnapshotQueryResult => {
    const {
      id, queries, changeTypes, modifier,
      callbackOptions,
    } = options;

    const callback = snapshotCallback || this.defaultSnapshotCallback;

    if (id) {
      const documentReference = this.methods.doc(collection, `${id}`);

      return onSnapshot(documentReference, (snapshot) => {
        const changeType = snapshot.exists ? FirestoreChangeTypes.FIRESTORE_CHANGE_TYPE_ADDED : FirestoreChangeTypes.FIRESTORE_CHANGE_TYPE_REMOVED;

        callback({
          id: snapshot.id,
          ...snapshot.data(),
          ...modifier || {},
          FIRESTORE_CHANGE_TYPE: changeType,
        }, callbackOptions);
      }, (error) => {
        console.log(error);
        console.log(documentReference);
      });
    }

    const queryData = queries ? queries(new QueryHelper<T>()) : null;
    const queryConstraints = this.getQueryConstraints(options, queryData);

    const query = this.methods.query(collection, ...queryConstraints);

    return onSnapshot(query, (snapshot) => {
      if (snapshot.empty) {
        callback({} as any, callbackOptions);
      }

      if (!snapshot.docChanges) {
        snapshot.forEach((doc) => {
          const changeType = doc.exists ? FirestoreChangeTypes.FIRESTORE_CHANGE_TYPE_ADDED : FirestoreChangeTypes.FIRESTORE_CHANGE_TYPE_REMOVED;

          callback({
            id: doc.id,
            ...doc.data(),
            ...modifier || {},
            FIRESTORE_CHANGE_TYPE: changeType,
          }, callbackOptions);
        });
      } else if (changeTypes) {
        snapshot.docChanges().forEach((change) => {
          callback({
            id: change.doc.id,
            ...change.doc.data(),
            FIRESTORE_CHANGE_TYPE: change.type as FirestoreChangeTypes,
          }, callbackOptions);
        });
      } else {
        const docChanges = snapshot.docChanges();

        if (docChanges.length) {
          const changes = reduce(
            docChanges,
            (results, change) => ({
              ...results,
              payload: {
                ...results.payload,
                [change.doc.id]: {
                  id: change.doc.id,
                  ...change.doc.data(),
                  ...modifier || {},
                  FIRESTORE_CHANGE_TYPE: change.type,
                },
              },
            }),
            { payload: {} },
          );

          callback(changes as SnapshotResult<T, never>, callbackOptions);
        }
      }
    }, (error) => {
      console.log(error);
      console.log(query);
    });
  };
}
